import React, { useState, useEffect } from 'react';
import { ArrowRight, Globe, TrendingUp, Shield, Users, Package, Ship, Award, CheckCircle, Star, ChevronRight, Leaf, Plane, Truck } from 'lucide-react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { Link } from 'react-router-dom';

const Home = () => {
    const [isVisible, setIsVisible] = useState({});

    // hero carousel state
    const [currentIcon, setCurrentIcon] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // why‑choose image carousel state
    const [currentWhyIcon, setCurrentWhyIcon] = useState(0);
    const [isWhyTransitioning, setIsWhyTransitioning] = useState(false);

    // Hero slider data with rich info
    const heroSlides = [
        {
            icon: <Plane className="w-16 h-16" />,
            mode: "Air Freight",
            tagline: "Swift & Reliable",
            desc: "Express delivery worldwide within 24–72 hrs",
            bg: "from-sky-500/20 to-blue-600/10",
            accent: "#6ee7f7",
            dots: ["Dubai", "Singapore", "London", "New York"],
            badge: "Fastest Route",
        },
        {
            icon: <Ship className="w-16 h-16" />,
            mode: "Sea Freight",
            tagline: "Cost-Effective Bulk",
            desc: "FCL & LCL shipments across 50+ global ports",
            bg: "from-teal-500/20 to-emerald-600/10",
            accent: "#34d399",
            dots: ["Mumbai", "Nhava Sheva", "Rotterdam", "Colombo"],
            badge: "Most Popular",
        },
        {
            icon: <Truck className="w-16 h-16" />,
            mode: "Land Transport",
            tagline: "Last Mile Mastery",
            desc: "Door-to-door domestic & cross-border logistics",
            bg: "from-amber-500/20 to-orange-500/10",
            accent: "#fbbf24",
            dots: ["Delhi", "Mumbai", "Ahmedabad", "Chennai"],
            badge: "Pan India",
        },
    ];

    // Why Choose slider data
    const whySlides = [
        {
            icon: <Plane className="w-20 h-20" />,
            color: "from-green-700 to-emerald-800",
        },
        {
            icon: <Ship className="w-20 h-20" />,
            color: "from-emerald-600 to-green-800",
        },
        {
            icon: <Truck className="w-20 h-20" />,
            color: "from-teal-600 to-green-700",
        },
    ];

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );

        const elements = document.querySelectorAll('.animate-on-scroll');
        elements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    // hero slider effect
    useEffect(() => {
        const interval = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentIcon(prev => (prev + 1) % heroSlides.length);
                setIsTransitioning(false);
            }, 400);
        }, 3500);
        return () => clearInterval(interval);
    }, []);

    // why‑choose slider effect
    useEffect(() => {
        const interval = setInterval(() => {
            setIsWhyTransitioning(true);
            setTimeout(() => {
                setCurrentWhyIcon(prev => (prev + 1) % whySlides.length);
                setIsWhyTransitioning(false);
            }, 400);
        }, 3500);
        return () => clearInterval(interval);
    }, []);

    const services = [
        {
            icon: <Globe className="w-12 h-12" />,
            title: "Global Export",
            description: "Expand your business worldwide with our comprehensive export solutions and international network.",
            color: "from-green-500 to-emerald-700"
        },
        {
            icon: <Ship className="w-12 h-12" />,
            title: "Logistics & Shipping",
            description: "Efficient shipping and logistics management to ensure timely delivery across continents.",
            color: "from-emerald-500 to-teal-700"
        },
        {
            icon: <Shield className="w-12 h-12" />,
            title: "Customs Clearance",
            description: "Expert customs clearance services to navigate complex regulations seamlessly.",
            color: "from-green-600 to-green-800"
        },
        {
            icon: <TrendingUp className="w-12 h-12" />,
            title: "Trade Consulting",
            description: "Strategic consulting to optimize your import-export operations and maximize profits.",
            color: "from-teal-500 to-emerald-700"
        }
    ];

    const stats = [
        { number: "500+", label: "Happy Clients", icon: <Users /> },
        { number: "10+", label: "Countries Served", icon: <Globe /> },
        { number: "1986+", label: "Shipments Delivered", icon: <Package /> },
        { number: "25+", label: "Years Experience", icon: <Award /> }
    ];

    const features = [
        "APEDA Certified Company",
        "24/7 Customer Support",
        "Competitive Pricing",
        "Expert Documentation",
        "Insurance Coverage",
        "FASSAI Approved",
        "Spice Board Registered",
        "IEC Code Holder"
    ];

    const trustedCompanies = [
        {
            name: "ITC Limited",
            industry: "FMCG & Agri Business",
            description: "Long-term export partner for premium agro commodities across GCC and European markets."
        },
        {
            name: "Cargill India Pvt Ltd",
            industry: "Global Food Corporation",
            description: "Reliable supply chain and bulk shipment coordination for international trade."
        },
        {
            name: "RB Agro Milling Pvt Ltd",
            industry: "Rice Processing Industry",
            description: "Expert documentation and smooth export handling for high-volume rice shipments."
        },
        {
            name: "Veenu Industries Pvt Ltd",
            industry: "Food Manufacturing",
            description: "Trusted collaboration ensuring consistent quality and on-time delivery worldwide."
        },
        {
            name: "Noble Flour Tech",
            industry: "Flour & Grain Exports",
            description: "Efficient global logistics solutions for wheat and flour exports."
        },
        {
            name: "Global Spice Traders",
            industry: "Spices & Commodities",
            description: "Premium spice export partnership serving Middle East and Asian markets."
        }
    ];

    const testimonials = [
        {
            name: "Rajesh Kumar",
            company: "ABC Textiles Ltd.",
            text: "Nurani Enterprise transformed our international business. Their expertise and dedication are unmatched!",
            rating: 5
        },
        {
            name: "Priya Sharma",
            company: "TechParts India",
            text: "Seamless export process and excellent customer service. Highly recommended for global trade.",
            rating: 5
        },
        {
            name: "Mohammed Ali",
            company: "Spice Traders Co.",
            text: "Professional team with deep knowledge of export regulations. Our shipments always arrive on time.",
            rating: 5
        }
    ];

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-white">

                {/* Hero Section */}
                <section className="relative bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 text-white overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-30"></div>
                    <div className="absolute inset-0">
                        <div className="absolute top-20 left-20 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
                        <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000"></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div className="space-y-8 animate-fade-in">
                                <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                                    Global Trade Without
                                    <span className="block bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
                                        Complications
                                    </span>
                                </h1>
                                <p className="text-xl text-green-100 leading-relaxed">
                                    <span className="font-semibold text-white">Nurani Enterprise</span> connects businesses across borders with reliable and transparent international trade solutions.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link to="/contact">
                                    <button className="bg-white text-green-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-green-50 transform hover:scale-105 transition-all duration-300 shadow-xl flex items-center justify-center gap-2">
                                        Get Started
                                        <ArrowRight className="w-5 h-5" />
                                    </button>
                                    </Link>
                                    {/* <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-green-900 transform hover:scale-105 transition-all duration-300">
                                        Learn More
                                    </button> */}
                                </div>
                            </div>

                            {/* ── HERO SLIDER ── */}
                            <div className="relative animate-float">
                                <div
                                    className="w-full rounded-3xl overflow-hidden shadow-2xl border border-white/20 backdrop-blur-sm flex items-center justify-center"
                                    style={{ background: 'rgba(255,255,255,0.06)', minHeight: '24rem' }}
                                >
                                    <div
                                        style={{
                                            opacity: isTransitioning ? 0 : 1,
                                            transform: isTransitioning ? 'scale(0.8)' : 'scale(1)',
                                            transition: 'opacity 0.4s ease, transform 0.4s ease',
                                            color: heroSlides[currentIcon].accent,
                                        }}
                                    >
                                        {React.cloneElement(heroSlides[currentIcon].icon, { className: 'w-48 h-48' })}
                                    </div>
                                </div>

                                {/* Glow */}
                                <div
                                    className="absolute -inset-1 rounded-3xl -z-10 blur-xl opacity-30 transition-all duration-700"
                                    style={{ background: `radial-gradient(circle, ${heroSlides[currentIcon].accent}, transparent 70%)` }}
                                />

                                {/* Dot indicators */}
                                <div className="flex justify-center gap-2 mt-4">
                                    {heroSlides.map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setCurrentIcon(i)}
                                            className="rounded-full transition-all duration-300"
                                            style={{
                                                width: i === currentIcon ? 24 : 8,
                                                height: 8,
                                                background: i === currentIcon ? heroSlides[currentIcon].accent : 'rgba(255,255,255,0.3)',
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0">
                        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
                        </svg>
                    </div>
                </section>

                {/* Stats Section */}
                <section className="py-16 bg-green-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                            {stats.map((stat, index) => (
                                <div
                                    key={index}
                                    id={`stat-${index}`}
                                    className="animate-on-scroll text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border-b-4 border-green-500"
                                    style={{
                                        opacity: isVisible[`stat-${index}`] ? 1 : 0,
                                        transform: isVisible[`stat-${index}`] ? 'translateY(0)' : 'translateY(20px)',
                                        transition: `all 0.6s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <div className="text-green-600 flex justify-center mb-4">
                                        {React.cloneElement(stat.icon, { className: "w-12 h-12" })}
                                    </div>
                                    <div className="text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                                    <div className="text-gray-600 font-medium">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Services Section */}
                {/* <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            id="services-header"
                            className="animate-on-scroll text-center mb-16"
                            style={{
                                opacity: isVisible['services-header'] ? 1 : 0,
                                transform: isVisible['services-header'] ? 'translateY(0)' : 'translateY(30px)',
                                transition: 'all 0.8s ease-out'
                            }}
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                Our <span className="text-green-600">Services</span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Comprehensive export-import solutions tailored to your business needs
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {services.map((service, index) => (
                                <div
                                    key={index}
                                    id={`service-${index}`}
                                    className="animate-on-scroll group"
                                    style={{
                                        opacity: isVisible[`service-${index}`] ? 1 : 0,
                                        transform: isVisible[`service-${index}`] ? 'translateY(0)' : 'translateY(30px)',
                                        transition: `all 0.6s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl p-8 h-full transform hover:-translate-y-2 transition-all duration-300 border border-green-50 hover:border-green-200">
                                        <div className={`bg-gradient-to-br ${service.color} text-white w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                                            {service.icon}
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-green-700 transition-colors">{service.title}</h3>
                                        <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                                        <a href="#" className="text-green-600 font-semibold flex items-center gap-2 group-hover:gap-4 transition-all">
                                            Learn More
                                            <ChevronRight className="w-5 h-5" />
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section> */}

                {/* Why Choose Us Section */}
                <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div
                                id="why-choose-content"
                                className="animate-on-scroll"
                                style={{
                                    opacity: isVisible['why-choose-content'] ? 1 : 0,
                                    transform: isVisible['why-choose-content'] ? 'translateX(0)' : 'translateX(-50px)',
                                    transition: 'all 0.8s ease-out'
                                }}
                            >
                                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                                    Why Choose <span className="text-green-600">Nurani Enterprise?</span>
                                </h2>
                                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                                    With over 25 years of experience, we provide trusted and efficient international trade solutions.
                                </p>

                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    {features.map((feature, index) => (
                                        <div key={index} className="flex items-center gap-3">
                                            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                                            <span className="text-gray-700 font-medium">{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* <button className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                                    Get a Free Quote
                                </button> */}
                            </div>

                            <div
                                id="why-choose-image"
                                className="animate-on-scroll relative"
                                style={{
                                    opacity: isVisible['why-choose-image'] ? 1 : 0,
                                    transform: isVisible['why-choose-image'] ? 'translateX(0)' : 'translateX(50px)',
                                    transition: 'all 0.8s ease-out'
                                }}
                            >
                                {/* ── WHY CHOOSE SLIDER ── */}
                                <div className="relative">
                                    {/* Decorative rotated card behind */}
                                    <div
                                        className={`absolute inset-0 rounded-3xl transform rotate-3 opacity-40 bg-gradient-to-br ${whySlides[currentWhyIcon].color}`}
                                        style={{ transition: 'background 0.5s ease' }}
                                    />

                                    <div
                                        className={`relative rounded-3xl overflow-hidden shadow-2xl border-2 border-white bg-gradient-to-br ${whySlides[currentWhyIcon].color} flex items-center justify-center`}
                                        style={{ minHeight: '24rem', transition: 'background 0.5s ease' }}
                                    >
                                        {/* Background texture */}
                                        <div className="absolute inset-0 opacity-10"
                                            style={{
                                                backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 1px, transparent 0px, transparent 50%)',
                                                backgroundSize: '12px 12px',
                                            }}
                                        />

                                        {/* Icon only */}
                                        <div
                                            style={{
                                                opacity: isWhyTransitioning ? 0 : 1,
                                                transform: isWhyTransitioning ? 'scale(0.7)' : 'scale(1)',
                                                transition: 'opacity 0.4s ease, transform 0.4s ease',
                                            }}
                                            className="relative z-10 text-white"
                                        >
                                            {React.cloneElement(whySlides[currentWhyIcon].icon, { className: 'w-48 h-48 text-white opacity-90' })}
                                        </div>
                                    </div>

                                    {/* Dot indicators */}
                                    <div className="flex justify-center gap-2 mt-4">
                                        {whySlides.map((_, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setCurrentWhyIcon(i)}
                                                className="rounded-full transition-all duration-300"
                                                style={{
                                                    width: i === currentWhyIcon ? 28 : 8,
                                                    height: 8,
                                                    background: i === currentWhyIcon ? '#34d399' : 'rgba(0,0,0,0.2)',
                                                }}
                                            />
                                        ))}
                                    </div>

                                    {/* Glow effect */}
                                    <div
                                        className="absolute -inset-2 rounded-3xl -z-10 blur-2xl opacity-25"
                                        style={{
                                            background: `linear-gradient(135deg, #34d399, transparent)`,
                                            transition: 'background 0.5s ease',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Testimonials Section */}
                {/* Trusted By Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                Trusted By <span className="text-green-600">Leading Companies</span>
                            </h2>
                            <p className="text-xl text-gray-600">
                                We proudly serve reputed organizations across industries and borders.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {trustedCompanies.map((company, index) => (
                                <div
                                    key={index}
                                    className="bg-gradient-to-br from-gray-50 to-green-50 
                               rounded-2xl p-8 shadow-lg 
                               hover:shadow-xl transition-all duration-300 
                               border border-green-100 
                               hover:-translate-y-2"
                                >
                                    <div className="mb-4">
                                        <h3 className="text-xl font-bold text-gray-900">
                                            {company.name}
                                        </h3>
                                        <p className="text-sm text-green-600 font-medium">
                                            {company.industry}
                                        </p>
                                    </div>

                                    <p className="text-gray-700 leading-relaxed">
                                        {company.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
                {/* CTA Section */}
                <section className="py-20 bg-gradient-to-r from-green-700 to-teal-800 text-white relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            Ready to Expand Your Business Globally?
                        </h2>
                        <p className="text-xl mb-8 text-green-100 max-w-2xl mx-auto">
                            Let's discuss how <span className="font-semibold text-white">Nurani Enterprise</span> can help you reach new markets and grow your international presence.
                        </p>
                        <Link to="/contact">
                            <button className="bg-white text-green-800 px-10 py-4 rounded-full font-bold text-lg hover:bg-green-50 transform hover:scale-105 transition-all duration-300 shadow-xl">
                                Contact Us Today
                            </button>
                        </Link>
                    </div>
                </section>

                <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
      `}</style>
            </div>
            <Footer />
        </>
    );
};

export default Home;